import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import { FaSync, FaTimes, FaDownload } from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { fetchStatusDurationReport } from '~/services/fund-requests';
import { StatusDurationReportRow } from '~/types/fundrequests';

const COLUMNS = [
    { key: 'fund_request_id', label: 'FR ID' },
    { key: 'client_name', label: 'Client Name' },
    { key: 'ntaf_id', label: 'NTAF ID' },
    { key: 'coordinator', label: 'Coordinator' },
    { key: 'payee', label: 'Payee' },
    { key: 'amount', label: 'Amount' },
    { key: 'status_label', label: 'Status' },
    { key: 'entered_at', label: 'Entered' },
    { key: 'exited_at', label: 'Exited' },
    { key: 'duration_days', label: 'Duration (days)' },
];

const renderCellContent = (row: StatusDurationReportRow, columnKey: string) => {
    switch (columnKey) {
        case 'fund_request_id':
            return row.fund_request_id;
        case 'client_name':
            return row.client_name || '';
        case 'ntaf_id':
            return row.ntaf_id || '';
        case 'coordinator':
            return row.coordinator || '';
        case 'payee':
            return row.payee || '';
        case 'amount':
            return row.amount != null
                ? `$${Number(row.amount).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  })}`
                : '';
        case 'status_label':
            return row.status_label || '';
        case 'entered_at':
            return row.entered_at
                ? new Date(row.entered_at).toLocaleDateString()
                : '';
        case 'exited_at':
            return row.exited_at
                ? new Date(row.exited_at).toLocaleDateString()
                : '';
        case 'duration_days':
            return row.duration_days != null
                ? Number(row.duration_days).toFixed(2)
                : 'active';
        default:
            return '';
    }
};

const StatusDurationResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState<StatusDurationReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleRefresh = useCallback(() => {
        setRefreshKey((prev) => prev + 1);
    }, []);

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                const filters = {
                    submitted_date_start:
                        searchParams.get('submitted_date_start') || '',
                    submitted_date_end:
                        searchParams.get('submitted_date_end') || '',
                    page,
                };

                const response = await fetchStatusDurationReport(filters);

                if (response.results) {
                    setResults(response.results);
                    setTotalCount(response.count || 0);
                    setTotalPages(Math.ceil((response.count || 0) / 20));
                } else {
                    setResults([]);
                    setTotalCount(0);
                    setTotalPages(1);
                }
            } catch (err) {
                console.error('Error fetching status duration report:', err);
                setError(
                    'Failed to generate report. Please try again.'
                );
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handlePageChange = (page: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(page));
        setSearchParams(newParams);
    };

    const { clearFilterParams } = useReportFilters();
    const handleClearFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    const [exporting, setExporting] = useState(false);

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            const filters = {
                submitted_date_start:
                    searchParams.get('submitted_date_start') || '',
                submitted_date_end:
                    searchParams.get('submitted_date_end') || '',
                page: 1,
            };

            // Fetch all pages
            let allResults: StatusDurationReportRow[] = [];
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await fetchStatusDurationReport({
                    ...filters,
                    page,
                });

                if (response.results && response.results.length > 0) {
                    allResults = [...allResults, ...response.results];
                    hasMore = response.next !== null;
                    page++;
                } else {
                    hasMore = false;
                }
            }

            // Generate CSV
            const headers = COLUMNS.map((col) => col.label);
            const rows = allResults.map((row) =>
                COLUMNS.map((col) => {
                    const content = renderCellContent(row, col.key);
                    const str = String(content);
                    if (str.includes(',') || str.includes('"')) {
                        return `"${str.replace(/"/g, '""')}"`;
                    }
                    return str;
                })
            );

            const csvContent = [
                headers.join(','),
                ...rows.map((row) => row.join(',')),
            ].join('\n');

            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute(
                'download',
                `status-duration-report-${new Date().toISOString().split('T')[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting CSV:', error);
        } finally {
            setExporting(false);
        }
    };

    const activeFilterCount = useMemo(() => {
        const filterKeys = ['submitted_date_start', 'submitted_date_end'];
        return filterKeys.filter(
            (key) => searchParams.has(key) && searchParams.get(key)
        ).length;
    }, [searchParams]);

    return (
        <>
            <Breadcrumb
                pageName='Status Duration Report'
                parentPage='Reports'
                parentPath='/new-report'
            />

            <div className='rounded-sm border border-stroke bg-white shadow-default'>
                {/* Header with actions */}
                <div className='border-b border-stroke px-6 py-4'>
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                        <div className='flex items-center gap-4'>
                            <h4 className='text-xl font-semibold text-black'>
                                {loading
                                    ? 'Generating...'
                                    : `${totalCount} Results`}
                            </h4>
                            {activeFilterCount > 0 && (
                                <span className='rounded-full bg-primary/10 px-3 py-1 text-sm text-primary'>
                                    {activeFilterCount} filter
                                    {activeFilterCount !== 1 ? 's' : ''} applied
                                </span>
                            )}
                        </div>
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={handleRefresh}
                                className='flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50'
                                title='Refresh results'
                            >
                                <FaSync
                                    className={
                                        loading ? 'animate-spin' : ''
                                    }
                                />
                                Refresh
                            </button>
                            <button
                                onClick={handleExportCSV}
                                disabled={
                                    results.length === 0 || exporting
                                }
                                className='flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50'
                                title='Export all results to CSV'
                            >
                                <FaDownload
                                    className={
                                        exporting ? 'animate-pulse' : ''
                                    }
                                />
                                {exporting ? 'Exporting...' : 'Export All'}
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className='flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50'
                                title='Clear all filters'
                            >
                                <FaTimes />
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error state */}
                {error && (
                    <div className='border-b border-stroke bg-red-50 px-6 py-4 text-red-600'>
                        {error}
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className='flex items-center justify-center py-12'>
                        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
                    </div>
                )}

                {/* Results table */}
                {!loading && results.length > 0 && (
                    <div className='overflow-x-auto'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    {COLUMNS.map((column) => (
                                        <th
                                            key={column.key}
                                            className='px-4 py-4 font-medium text-black'
                                        >
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row, index) => (
                                    <tr
                                        key={`${row.fund_request_id}-${row.entered_at}-${index}`}
                                        className='border-b border-stroke hover:bg-gray-1'
                                    >
                                        {COLUMNS.map((column) => (
                                            <td
                                                key={column.key}
                                                className='px-4 py-3 text-sm'
                                            >
                                                {renderCellContent(
                                                    row,
                                                    column.key
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty state */}
                {!loading && results.length === 0 && !error && (
                    <div className='flex flex-col items-center justify-center py-12 text-gray-500'>
                        <p className='text-lg'>No results found</p>
                        <p className='mt-2 text-sm'>
                            Try adjusting your date range filters.
                        </p>
                    </div>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className='border-t border-stroke px-6 py-4'>
                        <Pagination
                            page={currentPage}
                            pages={totalPages}
                            setPage={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default StatusDurationResults;
